import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import { useMyTasks, useQueues, useTaskDepartments, type TaskGroup } from '../api/taskQueries.ts';
import { useSeen } from '../tasks/seen.ts';
import { TaskRow } from '../components/tasks/TaskRow.tsx';
import { TaskCard } from '../components/tasks/TaskCard.tsx';
import { NewTaskSheet } from '../components/tasks/NewTask.tsx';
import { plain } from '../components/tasks/Fields.tsx';
import { DepartmentOverduePanel } from '../components/tasks/DepartmentOverdue.tsx';
import { DepartmentSelect } from '../components/tasks/DepartmentSelect.tsx';
import { groupsInDepartment, type DepartmentChoice } from '../tasks/departmentFilter.ts';


/**
 * My tasks (147, A-#77, ruling 12): the reader's work in the nine groups, as GET /tasks/mine makes them, in its order
 * and with its labels. The screen never re-groups: weeks are Monday to Sunday in New York time, and an overdue
 * Critical task is in Overdue once, marked Emergency (Arthur, A-#78), all the server's. Every row drawn is reported
 * seen in one call (ruling 17); a row opens the task card, which records "started" and never changes the status.
 * Actions are recorded from a department section of the client page (A-#144), not here.
 *
 * A failure is never shown as an empty list.
 */
export function MyTasks() {
  const mine = useMyTasks();
  const queues = useQueues();
  // New task only where the server's create list holds a department (150: the Property team works its own tasks and
  // makes none by hand; a queue, a department's list or triage are not theirs either).
  const mayCreate = (useTaskDepartments().data?.create ?? []).length > 0;
  const [open, setOpen] = useState<string | null>(null);
  const [sheet, setSheet] = useState<'new' | null>(null);
  // P-23: by department, on the page (the route returns them all); seen follows only the rows shown (A-#257 1A)
  const [department, setDepartment] = useState<DepartmentChoice>('');
  const all = mine.data?.groups ?? [];
  const groups = groupsInDepartment(all, department);
  const drawn = groups.flatMap((g) => g.tasks);
  useSeen(mine.data ? drawn : undefined);
  const openCount = groups.filter((g) => g.key !== 'done_this_week').reduce((n, g) => n + g.tasks.length, 0);
  const takeover = mine.error !== null && (isSessionFailure(mine.error) || !mine.data);
  const mayQueue = (queues.data?.queues ?? []).some((q) => q.may_open);

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">My tasks</h1>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-neutral-700 tabular-nums">{takeover ? 'Not loaded' : mine.isPending ? 'Loading…' : department ? `${openCount} open in ${department}` : `${openCount} open`}</p>
            <span className="ml-auto flex flex-wrap gap-2">
              {mayCreate && <button type="button" className={plain} onClick={() => setSheet('new')}>New task</button>}
              {mayQueue && <Link to="/tasks/queue" className={`${plain} inline-flex items-center`}>Department queue</Link>}
              {mayQueue && <Link to="/tasks/triage" className={`${plain} inline-flex items-center`}>Triage</Link>}
            </span>
          </div>
          {mine.data && <div className="mt-2"><DepartmentSelect value={department} onChange={setDepartment} /></div>}
          {mine.data && <GroupStrip groups={groups} />}
        </header>
        <div className="page-body">
          {/* A-#83 A5: at the top of My tasks for any director (the two new director roles land on no dashboard). */}
          <DepartmentOverduePanel className="mx-4 mt-3 mb-1 lg:mx-5" />
          {!takeover && <Staleness updatedAt={mine.dataUpdatedAt} isFetching={mine.isFetching} error={mine.error} onRefresh={() => void mine.refetch()} />}
          {mine.error && takeover ? (
            <LoadFailure what="Your tasks" error={mine.error} onRetry={() => void mine.refetch()} retrying={mine.isFetching} />
          ) : mine.data && drawn.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-700">
              {department ? `None of your tasks are in ${department}.` : 'No tasks are assigned to you, and none were done this week.'}
            </p>
          ) : (
            groups.filter((g) => g.tasks.length > 0).map((g) => (
              <section key={g.key} id={`group-${g.key}`} aria-labelledby={`h-${g.key}`}>
                <h2 id={`h-${g.key}`} className={`border-t border-neutral-200 px-4 pt-3 pb-1 text-xs font-bold tracking-[0.14em] uppercase lg:px-5 ${
                  g.key === 'overdue' || g.key === 'emergency' ? 'text-critical-edge' : 'text-neutral-700'}`}>
                  {g.label} · {g.tasks.length}
                </h2>
                <ol className="divide-y divide-neutral-200">
                  {g.tasks.map((t) => <TaskRow key={t.id} task={t} today={mine.data!.today} onOpen={setOpen} />)}
                </ol>
              </section>
            ))
          )}
        </div>
      </div>
      {open && <TaskCard taskId={open} onClose={() => setOpen(null)} />}
      {sheet === 'new' && <NewTaskSheet onClose={() => setSheet(null)} />}
    </div>
  );
}

/** Every group's count, in order, empty ones included, so the shape of the week is seen at a glance. */
function GroupStrip({ groups }: { groups: TaskGroup[] }) {
  return (
    <ul aria-label="Tasks by group" className="mt-2 flex flex-wrap gap-2">
      {groups.map((g) => (
        <li key={g.key}>
          {g.tasks.length > 0 ? (
            <a href={`#group-${g.key}`} className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-neutral-300 bg-surface px-3 text-sm text-neutral-900">
              {g.label} <span className="font-semibold tabular-nums">{g.tasks.length}</span>
            </a>
          ) : (
            <span className="inline-flex min-h-[44px] items-center gap-1 px-3 text-sm text-neutral-700">{g.label} <span className="tabular-nums">0</span></span>
          )}
        </li>
      ))}
    </ul>
  );
}
